import {
  FieldValueExt,
  type FieldData,
  type FieldValue,
} from '@castoredc/castor_prose';

/**
 * Updates field data by removing, inserting, or updating a field.
 *
 * @param fieldsData - The current fields data
 * @param fieldName - The name of the field to update
 * @param fieldValue - The new value (undefined means remove the field)
 * @returns A new FieldData object with the updated state
 */
export function updateFieldData(
  fieldsData: FieldData,
  fieldName: string,
  fieldValue: FieldValue | undefined
): FieldData {
  if (fieldValue === undefined) {
    // Removing field - create new object without the field
    const { [fieldName]: _removed, ...rest } = fieldsData.values;
    return { values: rest };
  }

  // Adding or updating field - immutable update
  return {
    values: {
      ...fieldsData.values,
      [fieldName]: fieldValue,
    },
  };
}

/**
 * Removes unrepresentable values and their names from the touched fields.
 */
export function dropUnrepresentableValues(
  fieldsData: FieldData,
  touchedFields: string[]
): {
  fieldsData: FieldData;
  touchedFields: string[];
  droppedFieldNames: string[];
} {
  const droppedFieldNames = Object.entries(fieldsData.values)
    .filter(([, value]) => FieldValueExt.is_unrepresentable(value))
    .map(([fieldName]) => fieldName);

  if (droppedFieldNames.length === 0) {
    return { fieldsData, touchedFields, droppedFieldNames };
  }

  return {
    fieldsData: {
      values: Object.fromEntries(
        Object.entries(fieldsData.values).filter(
          ([fieldName]) => !droppedFieldNames.includes(fieldName)
        )
      ),
    },
    touchedFields: touchedFields.filter(
      fieldName => !droppedFieldNames.includes(fieldName)
    ),
    droppedFieldNames,
  };
}
